<template lang="pug">
  .leaderboard
    WtTitle(card-title) {{ title }}
    .subtitle.is-size-6(v-if="subTitle") {{ subTitle }}
    .body
      HorizontalBarChart(
        v-if="loadedStatus && !_.isEmpty(data.data)"
        :chart-data="chartData"
        :isTeamChallenge="isTeamChallenge"
        :isOrganizationLeaderboard="isOrganizationLeaderboard"
        @add-background-color="backgroundColor = $event"
        :showPacer="showPacer"
        :height="height"
        :maxX="maxX"
        :goalLine="goalLine"
        :isPvsPChallenge="isPvsPChallenge"
        :profileNames="getProfileNames"
      )
      .empty-state.has-text-grey.is-size-7(v-if="_.isEmpty(data.data)")
        p Hey there,
        br
        p A chart will show here when data is available.

    slot(name="footer").wt-leaderboard-footer
</template>

<script>
import WtTitle from "@@/WtTitle/WtTitle";
import HorizontalBarChart from "@/charts/HorizontalBarChart";

export default {
  name: "WtLeaderboard",

  components: { WtTitle, HorizontalBarChart },

  props: {
    height: {
      type: Number,
      default: undefined
    },

    title: {
      type: String,
      required: true
    },

    subTitle: {
      type: String,
      default: null
    },

    data: {
      type: Object,
      required: true
    },

    loadedStatus: {
      type: Boolean,
      default: false
    },

    isChallenge: {
      type: Boolean,
      default: false
    },

    isTeamChallenge: {
      type: Boolean,
      default: false
    },

    isPvsPChallenge: {
      type: Boolean,
      default: false
    },

    isPoints: {
      type: Boolean,
      default: false
    },

    isProfile: {
      type: Boolean,
      default: false
    },

    memberUsername: {
      type: String,
      default: ""
    },

    pacerValue: {
      type: Number,
      default: 0
    },

    maxX: {
      type: Number,
      default: undefined
    },

    goalLine: {
      type: Number,
      default: undefined
    },

    barPercentage: {
      type: Number,
      default: undefined
    },

    showTooltipLabel: {
      type: Boolean,
      default: true
    },

    isOrganizationLeaderboard: {
      type: Number,
      default: 0
    },

    stepsOrActivitiesText: {
      type: String,
      default: "Steps"
    },

    trackingCalc: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      backgroundColor: ""
    };
  },

  computed: {
    showPacer() {
      return this.pacerValue > 0;
    },

    dataType() {
      if (this.isChallenge) {
        if (this.isPoints) {
          return {
            key: "tally",
            label: "Total Points In Challenge"
          };
        } else if (this.isTeamChallenge) {
          return {
            key: "tally",
            label: this.trackingCalc
              ? "Team Total in Challenge"
              : "Team Average In Challenge"
          };
        } else if (this.isPvsPChallenge) {
          return {
            key: "tally",
            label: "Program Average In Challenge"
          };
        } else {
          return {
            key: "tally",
            label: `Total ` + this.stepsOrActivitiesText + ` In Challenge`
          };
        }
      }
      return {
        key: "sevenDayAverage",
        label: "Seven Day Average"
      };
    },

    chartData() {
      const members = this._.orderBy(this.data.data, this.dataType.key, "desc");

      const labels = members.map(member => this.entityName(member));
      const data = members.map(member => member[this.dataType.key]);

      // Add Pacer to Leaderboard
      if (this.showPacer) {
        labels.unshift("Pacer");
        data.unshift(this.pacerValue);
      }

      const defaultPercentage = data.length < 5 ? 0.3 : 0.6;

      const datasets = [
        {
          label: this.showTooltipLabel ? this.dataType.label : "",
          backgroundColor: this.backgroundColor,
          barPercentage: this.barPercentage || defaultPercentage,
          data
        }
      ];

      const footers = [""];
      // if is a team leaderboard display organization name in footer
      if (this.isTeamChallenge && !this.isOrganizationLeaderboard) {
        this.data.data.forEach(team => {
          if (team.organization) {
            footers.push(team.organization.name);
          } else {
            footers.push("");
          }
        });
      }

      return { labels, datasets, footers };
    },

    getProfileNames() {
      const profileNames = [];

      if (this.data && this.data.data) {
        this.data.data.forEach(member => {
          if (!this.isTeamChallenge) {
            profileNames.push(member.profileName);
          } else {
            profileNames.push("");
          }
        });
      }
      return profileNames;
    }
  },

  methods: {
    entityName(entity) {
      if (this.isPvsPChallenge) {
        return entity.programName;
      }
      if (this.isTeamChallenge) {
        return entity.teamName;
      }
      return entity.name;
    }
  }
};
</script>

<style lang="sass" scoped>
.leaderboard
  .body
    margin-bottom: 1rem
</style>
